import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Runtime on/off state for the optimistic UI, driven exclusively by saved backend settings.
 *
 * BOARD (planning board) is decided by TWO layers, and both must say yes:
 *
 *  - GENERAL (`enableOptimisticUiPlanningBoard`, tenant-wide, Settings → Optimistic UI):
 *    the master switch for the whole company. Off = the feature does not exist for anyone —
 *    the activity panel and its dock tab are hidden and no user can switch it on.
 *  - USER (`enableOptimisticUiPlanningBoardUser`, per user, the slider on the activity panel):
 *    a personal opt-out while the general switch is on. Defaults to ON, so switching the
 *    general setting on gives every user the feature until they turn themselves off.
 *
 * The board/PB-screen code base reads `environment.enableOptimisticPlanning` in ~150 places, so
 * the EFFECTIVE board state is mirrored into that shared flag instead of rewriting every call
 * site — the env flag IS the effective board flag. It is a runtime mirror only: it is no longer
 * a configuration knob, it is not present in the customer-specific config JSONs, and whatever a
 * stale deployment still puts there is neutralized in the constructor.
 *
 * DEPOT (`enableOptimisticUi`, tenant-wide) has no per-user layer — the depot readers are few
 * and go through `depotEnabled$`.
 *
 * No localStorage — a second client-side store previously fought the backend value on refresh.
 */
@Injectable({ providedIn: 'root' })
export class OptimisticPlanningService {

  /** Tenant-wide master switch for the planning board. */
  readonly boardGeneralEnabled$ = new BehaviorSubject<boolean>(false);

  /** Per-user preference for the planning board; only has an effect while the general switch is on. */
  readonly boardUserEnabled$ = new BehaviorSubject<boolean>(true);

  /** Effective planning-board state (general AND user). Mirrored into environment.enableOptimisticPlanning. */
  readonly boardEnabled$ = new BehaviorSubject<boolean>(false);

  /** Depot In/Out optimistic state. */
  readonly depotEnabled$ = new BehaviorSubject<boolean>(false);

  /** True when the company allows optimistic UI on the board — gates the panel and its dock tab. */
  get isBoardGeneralEnabled(): boolean { return this.boardGeneralEnabled$.value; }

  /** The user's own preference, regardless of the general switch (drives the slider position). */
  get isBoardUserEnabled(): boolean { return this.boardUserEnabled$.value; }

  get isBoardEnabled(): boolean { return this.boardEnabled$.value; }
  get isDepotEnabled(): boolean { return this.depotEnabled$.value; }
  get anyEnabled(): boolean { return this.isBoardEnabled || this.isDepotEnabled; }

  constructor() {
    // Neutralize whatever a stale build/runtime config put in the shared flag — the two board
    // settings are the only writers from here on.
    (environment as any).enableOptimisticPlanning = false;
  }

  /** Apply the tenant-wide `enableOptimisticUiPlanningBoard` setting. */
  setBoardGeneralEnabled(value: boolean): void {
    const next = !!value;
    if (next === this.isBoardGeneralEnabled) return;
    this.boardGeneralEnabled$.next(next);
    this.applyBoardState();
  }

  /** Apply the user's own `enableOptimisticUiPlanningBoardUser` preference. */
  setBoardUserEnabled(value: boolean): void {
    const next = !!value;
    if (next === this.isBoardUserEnabled) return;
    this.boardUserEnabled$.next(next);
    this.applyBoardState();
  }

  /** Apply the tenant-wide `enableOptimisticUi` setting (Depot In/Out). */
  setDepotEnabled(value: boolean): void {
    const next = !!value;
    if (next === this.isDepotEnabled) return;
    this.depotEnabled$.next(next);
  }

  private applyBoardState(): void {
    const effective = this.isBoardGeneralEnabled && this.isBoardUserEnabled;
    if (effective === this.isBoardEnabled) return;
    (environment as any).enableOptimisticPlanning = effective;
    this.boardEnabled$.next(effective);
  }
}
